import axios from 'axios';
import apiClient, { handleApiError } from '../utils/apiClient';
import type { 
  DoctorShiftUploadRequest, 
  DoctorShiftUploadResponse,
  DoctorShiftRecord,
  DoctorShift,
  DoctorShiftListResponse,
  DoctorShiftCreateRequest,
  DoctorShiftUpdateRequest,
  DoctorShiftFilterOptions,
  AvailableDoctorsRequest,
  AvailableDoctorsResponse
} from '../types/doctorShift';

/**
 * 医師シフトAPI サービス
 * 
 * 意図：
 * - 医師シフトCSVのアップロード処理を管理
 * - APIとの通信を抽象化
 * - エラーハンドリングの一元化
 * 
 * 注意点：
 * - タブ区切りCSVを想定
 * - Bearer token認証が必要
 * - 最大5MBまでのファイルサイズ制限
 */

/**
 * APIレスポンス型定義
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

/**
 * 医師シフトCSVアップロードAPI
 */
export const doctorShiftApi = {
  /**
   * CSVファイルをアップロード
   * @param csvData CSV文字列データ
   * @param encoding ファイルエンコーディング
   * @returns アップロード結果
   */
  uploadCsv: async (
    csvData: string,
    encoding: 'utf-8' | 'shift-jis' = 'utf-8'
  ): Promise<DoctorShiftUploadResponse> => {
    try {
      const request: DoctorShiftUploadRequest = {
        csvData,
        encoding
      };

      const response = await apiClient.post<ApiResponse<any>>(
        '/api/doctor-shifts/csv/upload',
        request
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'アップロードに失敗しました');
      }

      // バックエンドのレスポンスをフロントエンドが期待する形式に変換
      const apiData = response.data.data;
      const result: DoctorShiftUploadResponse = {
        id: apiData.uploadId || '',
        status: 'completed' as const,  // 成功時は常に'completed'
        message: apiData.message || 'シフトデータを正常に登録しました',
        processedCount: apiData.processedCount,
        totalCount: apiData.totalCount,
        errors: []
      };

      console.log('📗 医師シフトアップロード成功レスポンス:', result);
      return result;
    } catch (error) {
      // デバッグ用：詳細なエラー情報をコンソールに出力
      if (axios.isAxiosError(error) && error.response?.status === 422) {
        console.error('🔴 医師シフトCSVアップロードエラー詳細:', {
          status: error.response.status,
          data: error.response.data,
          errors: error.response.data?.errors,
          message: error.response.data?.message,
          request: {
            csvData: csvData.substring(0, 200) + '...',
            encoding: encoding
          }
        });
      }
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * アップロード処理のステータスを確認
   * @param uploadId アップロードID
   * @returns ステータス情報
   */
  getUploadStatus: async (uploadId: string): Promise<DoctorShiftUploadResponse> => {
    try {
      const response = await apiClient.get<ApiResponse<DoctorShiftUploadResponse>>(
        `/api/doctor-shifts/csv/upload/${uploadId}`
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'ステータス取得に失敗しました');
      }

      return response.data.data;
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * CSVデータをバリデーション（クライアントサイド）
   * @param records 医師シフトレコードの配列
   * @returns バリデーション結果
   */
  validateCsvData: (records: DoctorShiftRecord[]): {
    isValid: boolean;
    errors: Array<{ row: number; field: string; message: string }>;
  } => {
    const errors: Array<{ row: number; field: string; message: string }> = [];
    const dateRegex = /^\d{4}\/\d{2}\/\d{2}$/;
    const timeRegex = /^([0-9]|0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;

    records.forEach((record, index) => {
      const row = index + 2; // ヘッダー行を考慮

      // 医師IDチェック
      if (!record.doctor_id || record.doctor_id.trim() === '') {
        errors.push({ row, field: '医師id', message: '医師IDは必須です' });
      }

      // シフト日付チェック
      if (!dateRegex.test(record.shift_date)) {
        errors.push({ row, field: 'シフト日付', message: '日付形式が正しくありません（YYYY/MM/DD）' });
      }

      // 診療開始時間チェック
      if (!timeRegex.test(record.start_time)) {
        errors.push({ row, field: '診療開始時間', message: '時刻形式が正しくありません（H:MM または HH:MM）' });
      }

      // 診療終了時間チェック
      if (!timeRegex.test(record.end_time)) {
        errors.push({ row, field: '診療終了時間', message: '時刻形式が正しくありません（H:MM または HH:MM）' });
      }

      // 開始時間と終了時間の整合性チェック
      if (timeRegex.test(record.start_time) && timeRegex.test(record.end_time)) {
        const start = record.start_time.split(':').map(Number);
        const end = record.end_time.split(':').map(Number);
        const startMinutes = start[0] * 60 + start[1];
        const endMinutes = end[0] * 60 + end[1];

        if (startMinutes >= endMinutes) {
          errors.push({ 
            row, 
            field: '時間', 
            message: '診療開始時間は終了時間より前である必要があります' 
          });
        }
      }
    });

    // 同一医師の時間重複チェック
    const shiftsByDoctorAndDate = new Map<string, Array<DoctorShiftRecord & { _index: number }>>();
    records.forEach((record, index) => {
      const key = `${record.doctor_id}-${record.shift_date}`;
      if (!shiftsByDoctorAndDate.has(key)) {
        shiftsByDoctorAndDate.set(key, []);
      }
      shiftsByDoctorAndDate.get(key)!.push({ ...record, _index: index });
    });

    shiftsByDoctorAndDate.forEach((shifts) => {
      if (shifts.length > 1) {
        // 時間でソート
        shifts.sort((a, b) => {
          const aStart = a.start_time.split(':').map(Number);
          const bStart = b.start_time.split(':').map(Number);
          return (aStart[0] * 60 + aStart[1]) - (bStart[0] * 60 + bStart[1]);
        });

        // 重複チェック
        for (let i = 0; i < shifts.length - 1; i++) {
          const current = shifts[i];
          const next = shifts[i + 1];
          
          const currentEnd = current.end_time.split(':').map(Number);
          const nextStart = next.start_time.split(':').map(Number);
          const currentEndMinutes = currentEnd[0] * 60 + currentEnd[1];
          const nextStartMinutes = nextStart[0] * 60 + nextStart[1];

          if (currentEndMinutes > nextStartMinutes) {
            errors.push({
              row: (next as any)._index + 2,
              field: '時間重複',
              message: `${current.doctor_id}医師の勤務時間が重複しています`
            });
          }
        }
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  },

  /**
   * 医師シフト一覧を取得
   * @param filters フィルター・検索・ページネーションオプション
   * @returns 医師シフト一覧データ
   */
  getDoctorShifts: async (filters: DoctorShiftFilterOptions = {}): Promise<DoctorShiftListResponse> => {
    try {
      const params = new URLSearchParams();
      
      // フィルターパラメータの設定
      if (filters.search) params.append('search', filters.search);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.time_range_start) params.append('time_range_start', filters.time_range_start);
      if (filters.time_range_end) params.append('time_range_end', filters.time_range_end);
      if (filters.doctor_ids?.length) filters.doctor_ids.forEach(id => params.append('doctor_ids[]', id));
      if (filters.sort_by) params.append('sort_by', filters.sort_by);
      if (filters.sort_order) params.append('sort_order', filters.sort_order);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.page_size) params.append('page_size', filters.page_size.toString());

      const response = await apiClient.get<ApiResponse<DoctorShiftListResponse>>(
        `/api/doctor-shifts?${params.toString()}`
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || '一覧取得に失敗しました');
      }

      return response.data.data;
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * 医師シフト詳細を取得
   * @param id 医師シフトID
   * @returns 医師シフト詳細データ
   */
  getDoctorShift: async (id: string): Promise<DoctorShift> => {
    try {
      const response = await apiClient.get<ApiResponse<DoctorShift>>(
        `/api/doctor-shifts/${id}`
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || '詳細取得に失敗しました');
      }

      return response.data.data;
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * 医師シフトを新規作成
   *
   * 選択した開始〜終了の範囲に含まれる募集枠に合わせて分割して作成されるため、
   * 複数件が返る。
   * @param shiftData 作成データ
   * @returns 作成件数と作成された医師シフトデータ
   */
  createDoctorShift: async (
    shiftData: DoctorShiftCreateRequest
  ): Promise<{ created_count: number; shifts: DoctorShift[] }> => {
    try {
      const response = await apiClient.post<ApiResponse<{ created_count: number; shifts: DoctorShift[] }>>(
        '/api/doctor-shifts',
        shiftData
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || '作成に失敗しました');
      }

      return response.data.data;
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * 医師シフトを更新
   * @param id 医師シフトID
   * @param shiftData 更新データ
   * @returns 更新された医師シフトデータ
   */
  updateDoctorShift: async (id: string, shiftData: DoctorShiftUpdateRequest): Promise<DoctorShift> => {
    try {
      const response = await apiClient.put<ApiResponse<DoctorShift>>(
        `/api/doctor-shifts/${id}`,
        shiftData
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || '更新に失敗しました');
      }

      return response.data.data;
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * 医師シフトを削除
   * @param id 医師シフトID
   * @returns 削除結果
   */
  deleteDoctorShift: async (id: string): Promise<void> => {
    try {
      const response = await apiClient.delete<ApiResponse<void>>(
        `/api/doctor-shifts/${id}`
      );

      if (!response.data.success) {
        throw new Error(response.data.error?.message || '削除に失敗しました');
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * 複数の医師シフトを一括削除
   * @param ids 医師シフトIDリスト
   * @returns 削除結果
   */
  bulkDeleteDoctorShifts: async (ids: string[]): Promise<void> => {
    try {
      const response = await apiClient.delete<ApiResponse<void>>(
        '/api/doctor-shifts/bulk',
        { data: { ids } }
      );

      if (!response.data.success) {
        throw new Error(response.data.error?.message || '一括削除に失敗しました');
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * 指定日時に利用可能な医師を取得
   * @param request 日付と時刻
   * @returns 利用可能な医師リスト（医師情報とシフト情報を含む）
   * 
   * 意図：
   * - 診療開始時間に基づいて勤務中の医師を検索
   * - 医師の基本情報（性別、優先度）も含めて返却
   * 
   * 注意点：
   * - 稼働ステータスが「契約済み」の医師のみ対象
   * - 時刻はシフト時間内であることを確認
   */
  getAvailableDoctors: async (request: AvailableDoctorsRequest): Promise<AvailableDoctorsResponse> => {
    try {
      const response = await apiClient.get<ApiResponse<AvailableDoctorsResponse>>(
        '/api/doctor-shifts/available',
        { params: request }
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || '利用可能な医師の取得に失敗しました');
      }

      return response.data.data;
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  }
};

export default doctorShiftApi;